// src/components/.../TwoStepDeleteDialog.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, XCircle, ShieldAlert, CreditCard } from 'lucide-react';

interface TwoStepDeleteDialogProps {
  isOpen: boolean;
  step: 1 | 2;
  onStep1Continue: () => void;
  onFinalConfirm: () => void;
  onCancel: () => void;
}

export const TwoStepDeleteDialog: React.FC<TwoStepDeleteDialogProps> = ({
  isOpen,
  step,
  onStep1Continue,
  onFinalConfirm,
  onCancel,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmValid = confirmText.trim() === 'אני מאשר';

  // ---- NEW: שליטה ב־drag בלי hooks של Framer ----
  const [canDrag, setCanDrag] = useState(false);
  const enableDrag = (e: React.PointerEvent) => {
    setCanDrag(true);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  // מחזירים drag=false ברגע שמרימים אצבע/עוזבים
  useEffect(() => {
    if (!canDrag) return;
    const off = () => setCanDrag(false);
    window.addEventListener('pointerup', off);
    window.addEventListener('pointercancel', off);
    return () => {
      window.removeEventListener('pointerup', off);
      window.removeEventListener('pointercancel', off);
    };
  }, [canDrag]);

  const scrollYRef = useRef(0);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const portalEl = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.setAttribute('data-two-step-delete-portal', 'true');
    return el;
  }, []);

  useEffect(() => {
    if (!portalEl) return;
    document.body.appendChild(portalEl);
    return () => {
      try { document.body.removeChild(portalEl); } catch {}
    };
  }, [portalEl]);

  // 🔒 נועל רקע ומסתיר BottomNav — לא חוסם גלילה בתוך המודאל
  useEffect(() => {
    if (!isOpen) return;

    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const navEl = document.querySelector<HTMLElement>('.bottom-nav');

    scrollYRef.current = window.scrollY || window.pageYOffset || 0;

    htmlEl.classList.add('hide-bottom-nav');
    bodyEl.classList.add('modal-open', 'hide-bottom-nav');

    if (navEl) {
      (navEl as any).dataset.prevDisplay = navEl.style.display || '';
      navEl.style.display = 'none';
      navEl.setAttribute('inert', '');
    }

    // נועלים גוף בלי קפיצה
    bodyEl.style.position = 'fixed';
    bodyEl.style.top = `-${scrollYRef.current}px`;
    bodyEl.style.left = '0';
    bodyEl.style.right = '0';
    bodyEl.style.width = '100%';
    bodyEl.style.overflow = 'hidden';

    // חוסמים גלילת רקע, מרשים בתוך המודאל
    const guardScroll = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) { e.preventDefault(); return; }
      if (target.closest('[data-modal-scroll]')) return; // ← מרשים גלילה בתוך המודאל
      e.preventDefault(); // ← חוסם רקע
    };

    document.addEventListener('touchmove', guardScroll, { passive: false });
    document.addEventListener('wheel', guardScroll, { passive: false });

    return () => {
      bodyEl.style.position = '';
      bodyEl.style.top = '';
      bodyEl.style.left = '';
      bodyEl.style.right = '';
      bodyEl.style.width = '';
      bodyEl.style.overflow = '';

      htmlEl.classList.remove('hide-bottom-nav');
      bodyEl.classList.remove('modal-open', 'hide-bottom-nav');

      if (navEl) {
        navEl.style.display = (navEl as any).dataset?.prevDisplay || '';
        navEl.removeAttribute('inert');
        delete (navEl as any).dataset?.prevDisplay;
      }

      window.scrollTo(0, scrollYRef.current || 0);

      document.removeEventListener('touchmove', guardScroll as any);
      document.removeEventListener('wheel', guardScroll as any);
    };
  }, [isOpen]);

  // reset אימות
  useEffect(() => {
    if (!isOpen || step === 1) setConfirmText('');
  }, [isOpen, step]);

  // ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen || !portalEl) return null;

  const modal = (
    <AnimatePresence mode="wait">
      {/* שכבת רקע */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      >
        {/* סדין */}
        <motion.div
          key={step}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 24, stiffness: 300 }}
          className="w-full max-w-md bg-white rounded-t-3xl shadow-2xl flex flex-col"
          style={{
            maxHeight: '85dvh',                      // ↓ dvh למובייל
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          onClick={(e) => e.stopPropagation()}
          drag={canDrag ? 'y' : false}               // ← drag דינמי (מה־handle בלבד)
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.04}
          dragMomentum={false}
          onDragEnd={(_, info) => { if (info.offset.y > 120) onCancel(); }}
          role="dialog"
          aria-modal="true"
        >
          {/* גריפ — מכאן מתחילים drag */}
          <div className="w-full flex justify-center py-2" onPointerDown={enableDrag}>
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>

          {/* ====== אזור תוכן נגלל ====== */}
          <div
            data-modal-scroll
            className="flex-1 overflow-y-auto overscroll-contain px-6 pb-24"
            style={{
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
            }}
          >
            {step === 1 ? (
              <>
                <div className="flex flex-col items-center text-center mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mb-4"
                  >
                    <AlertTriangle className="h-10 w-10 text-amber-600" />
                  </motion.div>
                  <h3 className="text-[22px] font-bold text-gray-900 mb-2">אזהרה - מחיקת מסוף</h3>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
                    <p className="text-[15px] text-gray-800 font-medium leading-relaxed">
                      מחיקת המסוף תגרום לניתוק מיידי מהשירותים הבאים:
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CreditCard className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-semibold text-gray-900 mb-1">סליקת כרטיסי אשראי</p>
                        <p className="text-[13px] text-gray-600">לא תוכל לקבל תשלומים בכרטיס אשראי דרך המערכת</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <XCircle className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-semibold text-gray-900 mb-1">הפקת חשבוניות אוטומטיות</p>
                        <p className="text-[13px] text-gray-600">חשבוניות לא יופקו אוטומטית לאחר קבלת תשלום</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center text-center mb-6">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center mb-4"
                  >
                    <ShieldAlert className="h-10 w-10 text-red-600" />
                  </motion.div>
                  <h3 className="text-[22px] font-bold text-red-600 mb-2">אישור סופי למחיקה</h3>
                </div>

                <div className="space-y-4 mb-6">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-red-50 border-2 border-red-300 rounded-2xl p-4"
                  >
                    <p className="text-[15px] text-red-900 font-bold mb-2">⚠️ פעולה בלתי הפיכה</p>
                    <p className="text-[14px] text-red-800 leading-relaxed">לאחר מחיקת המסוף, לא תוכל לשחזר את ההגדרות והנתונים.</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gray-50 rounded-2xl p-4 space-y-2"
                  >
                    <p className="text-[14px] font-semibold text-gray-900">💡 חשוב לדעת:</p>
                    <ul className="text-[13px] text-gray-700 space-y-1.5">
                      <li className="flex items-start gap-2"><span className="text-red-500 font-bold mt-0.5">•</span><span>לקוחות לא יוכלו לשלם בכרטיס אשראי</span></li>
                      <li className="flex items-start gap-2"><span className="text-red-500 font-bold mt-0.5">•</span><span>חשבוניות לא יופקו אוטומטית</span></li>
                      <li className="flex items-start gap-2"><span className="text-red-500 font-bold mt-0.5">•</span><span>תצטרך להזמין מסוף חדש לחידוש השירות</span></li>
                    </ul>
                  </motion.div>
                </div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="space-y-3">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <p className="text-[13px] text-center text-red-800 font-medium mb-2">על מנת לאשר את המחיקה, הקלד:</p>
                    <p className="text-[16px] text-center text-red-900 font-bold">אני מאשר</p>
                  </div>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="הקלד: אני מאשר"
                    className="w-full px-4 py-3 text-center text-[16px] font-semibold border-2 border-red-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    dir="rtl"
                  />
                </motion.div>
              </>
            )}
          </div>

          {/* ====== פוטר קבוע ====== */}
          <div className="bg-gradient-to-b from-gray-50 to-white px-6 pt-3 pb-5 border-t border-gray-100">
            {step === 1 ? (
              <div className="flex flex-col gap-3">
                <button onClick={onStep1Continue} className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-amber-200 hover:shadow-xl active:scale-95 transition-all">
                  המשך למחיקה
                </button>
                <button onClick={onCancel} className="w-full py-3.5 bg-white text-gray-700 rounded-2xl font-semibold text-[15px] border-2 border-gray-300 hover:bg-gray-50 active:scale-95 transition-all">
                  ביטול
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <button
                  onClick={isConfirmValid ? onFinalConfirm : undefined}
                  disabled={!isConfirmValid}
                  className={`w-full py-3.5 rounded-2xl font-bold text-[16px] shadow-lg transition-all ${
                    isConfirmValid ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-red-200 hover:shadow-xl active:scale-95 cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                  }`}
                >
                  {isConfirmValid ? 'אישור מחיקה סופית' : 'הקלד "אני מאשר" כדי להמשיך'}
                </button>
                <button onClick={onCancel} className="w-full py-3.5 bg-white text-gray-700 rounded-2xl font-semibold text-[15px] border-2 border-gray-300 hover:bg-gray-50 active:scale-95 transition-all">
                  ביטול ושמירת המסוף
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(modal, portalEl);
};
